import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';

function AcceptInvitation() {
  usePageTitle('Accept Invitation');
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);       // invitation details
  const [infoError, setInfoError] = useState('');
  const [loadingInfo, setLoadingInfo] = useState(true);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`/api/invitations/${token}/`)
      .then(res => setInfo(res.data))
      .catch(err => {
        const msg = err.response?.data?.error || t('invitation.invalidLink');
        setInfoError(msg);
      })
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      setSubmitError(t('auth.passwordsNoMatch'));
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`/api/invitations/${token}/accept/`, {
        password,
        password_confirm: passwordConfirm,
      });
      // Store JWT tokens and auto-login
      const user = { access: res.data.access };
      localStorage.setItem('user', JSON.stringify(user));
      // Store refresh token if present
      if (res.data.refresh) {
        localStorage.setItem('refresh', res.data.refresh);
      }
      setDone(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      const errData = err.response?.data?.error;
      setSubmitError(Array.isArray(errData) ? errData.join(' ') : errData || t('invitation.somethingWrong'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {t('invitation.activateAccount')}
        </Typography>

        {loadingInfo && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {infoError && (
          <Alert severity="error">{infoError}</Alert>
        )}

        {done && (
          <Alert severity="success">
            {t('invitation.accountActivated')}
          </Alert>
        )}

        {info && !done && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('invitation.activateInfo', {
                number: info.apartment.unit_number,
                union: info.apartment.union_name,
                email: info.email,
              })}
            </Alert>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
                autoFocus
              />
              <TextField
                fullWidth
                label={t('auth.confirmPassword')}
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                sx={{ mb: 2 }}
              />

              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                size="large"
              >
                {submitting ? <CircularProgress size={22} color="inherit" /> : t('invitation.activateButton')}
              </Button>
            </form>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default AcceptInvitation;
